import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, Zap, Lock, Globe, CheckCircle, FileSignature, Clock, Users, Building2, TrendingUp, Sparkles, ArrowRight, Check, X } from 'lucide-react';
import ComplianceSection, { ComplianceBadgeStrip } from '../components/ComplianceSection';
import { toast } from 'sonner';
import axios from 'axios';

const LandingPage = () => {
  const { login, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const authSectionRef = useRef(null);

  // Check for session expiry message on mount
  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      toast.warning('Your session has expired due to inactivity. Please log in again.', {
        duration: 6000,
      });
      // Remove the query parameter from URL
      searchParams.delete('session_expired');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Create Org
  const [orgName, setOrgName] = useState('');
  const [regionId, setRegionId] = useState('us-east-1');

  // Login
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/orgs`, {
        name: orgName,
        region_id: regionId,
        admin_email: clientId,  // Using clientId state for email
        password: clientSecret   // Using clientSecret state for password
      });

      toast.success('Organization created successfully!');
      toast.success(`You can now login with your email and password`);
      
      // Keep email in the form, clear password for security
      setClientSecret('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/token`, {
        email: clientId,  // Using clientId state variable for email
        password: clientSecret,  // Using clientSecret state variable for password
        grant_type: 'client_credentials'
      });
      
      // Decode JWT to extract org_id
      const token = response.data.access_token;
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      login(token, payload.org_id);
      toast.success('Logged in successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState('create');

  const scrollToAuth = (tab = 'create') => {
    setActiveTab(tab);
    setTimeout(() => {
      authSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      {/* Header / Navigation */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-purple-200 shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vivyasign_logo.png" alt="VivyaSigns" className="h-10 w-10" />
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent">
              VivyaSigns
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => scrollToAuth('login')}
              className="text-gray-700 hover:text-purple-600 hover:bg-purple-50"
            >
              Login
            </Button>
            <Button
              onClick={() => scrollToAuth('create')}
              className="bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:from-purple-700 hover:via-pink-600 hover:to-purple-700 text-white shadow-lg"
            >
              Sign Up Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-20 pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <img src="/vivyasign_logo.png" alt="VivyaSigns Logo" className="h-20 w-20 animate-pulse" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Sign Documents Digitally,
            <span className="bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent"> Anywhere, Anytime</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            VivyaSigns streamlines your document workflow with legally binding electronic signatures. Fast, secure, and compliant with global standards.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              onClick={() => scrollToAuth('create')}
              className="bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:from-purple-700 hover:via-pink-600 hover:to-purple-700 text-white px-8 shadow-xl hover:shadow-2xl transition-all"
            >
              Get Started Free <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-6 justify-center text-sm text-gray-600">
            <span className="flex items-center gap-2">
              <CheckCircle size={16} className="text-purple-500" />
              No credit card required
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle size={16} className="text-pink-500" />
              Free 14-day trial
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle size={16} className="text-purple-500" />
              Cancel anytime
            </span>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="container mx-auto px-4 py-12">
        <div className="flex flex-wrap gap-8 justify-center items-center opacity-60">
          <div className="flex items-center gap-2">
            <Shield size={24} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">SOC 2 Type II</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock size={24} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">HIPAA Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield size={24} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">GDPR Ready</span>
          </div>
          <div className="flex items-center gap-2">
            <FileSignature size={24} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">ESIGN & eIDAS</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Everything You Need for Digital Signatures
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Powerful features designed to accelerate your document workflows and enhance security
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card className="border-none shadow-lg hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-white to-purple-50">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                <Zap className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-900">Lightning-Fast Signing</h3>
              <p className="text-gray-700">
                Complete signatures in under 60 seconds. Our streamlined interface eliminates friction and speeds up approvals.
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-white to-pink-50">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center mb-4">
                <Shield className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-900">Bank-Level Security</h3>
              <p className="text-gray-700">
                256-bit encryption, multi-factor authentication, and comprehensive audit trails protect every transaction.
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-white to-purple-50">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                <Users className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-900">Multi-Party Workflows</h3>
              <p className="text-gray-700">
                Route documents to multiple signers with custom signing orders and automated reminders.
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-white to-pink-50">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <Globe className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-900">Global Compliance</h3>
              <p className="text-gray-700">
                Meet legal requirements in 180+ countries with ESIGN, UETA, and eIDAS compliance built-in.
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-white to-purple-50">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                <Clock className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-900">Template Library</h3>
              <p className="text-gray-700">
                Save time with reusable templates. Pre-configure fields and settings for recurring documents.
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-white to-pink-50">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-600 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-900">Real-Time Analytics</h3>
              <p className="text-gray-700">
                Track document status, completion rates, and team performance with detailed insights.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How VivyaSigns Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get documents signed in three simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-500 to-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3 text-purple-900">Upload Your Document</h3>
              <p className="text-gray-700">
                Drag and drop your PDF or Word document. Add signature fields, text boxes, and checkboxes with our intuitive editor.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 via-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3 text-purple-900">Send for Signature</h3>
              <p className="text-gray-700">
                Add recipients and customize the message. Set signing order and deadlines. Send invitations instantly via email.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-semibold mb-3 text-purple-900">Get It Signed</h3>
              <p className="text-gray-700">
                Signers receive secure links to sign from any device. Track progress in real-time and receive completed documents automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Trusted Across Industries
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From startups to enterprises, VivyaSigns powers document workflows worldwide
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-2 border-gray-200 hover:border-indigo-500 transition-colors">
            <CardContent className="pt-6">
              <Building2 className="text-indigo-600 mb-3" size={32} />
              <h3 className="font-semibold mb-2">Real Estate</h3>
              <p className="text-sm text-gray-600">Close deals faster with digital leases, contracts, and closing documents.</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-gray-200 hover:border-purple-500 transition-colors">
            <CardContent className="pt-6">
              <Users className="text-purple-600 mb-3" size={32} />
              <h3 className="font-semibold mb-2">Human Resources</h3>
              <p className="text-sm text-gray-600">Streamline onboarding with offer letters, NDAs, and employee agreements.</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-gray-200 hover:border-pink-500 transition-colors">
            <CardContent className="pt-6">
              <Shield className="text-pink-600 mb-3" size={32} />
              <h3 className="font-semibold mb-2">Legal Services</h3>
              <p className="text-sm text-gray-600">Secure client agreements, court filings, and confidential disclosures.</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-gray-200 hover:border-blue-500 transition-colors">
            <CardContent className="pt-6">
              <TrendingUp className="text-blue-600 mb-3" size={32} />
              <h3 className="font-semibold mb-2">Sales & Marketing</h3>
              <p className="text-sm text-gray-600">Accelerate revenue with instant contracts, proposals, and purchase orders.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Security, Quality & Compliance */}
      <div className="bg-white">
        <ComplianceSection />
      </div>

      {/* Auth Section */}
      <section ref={authSectionRef} className="container mx-auto px-4 py-20 scroll-mt-20">
        <Card className="max-w-md mx-auto shadow-2xl border-none" data-testid="auth-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Get Started with VivyaSigns</CardTitle>
            <CardDescription>Create your organization or sign in to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="create" data-testid="tab-create-org">Sign Up</TabsTrigger>
                <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
              </TabsList>

              <TabsContent value="create">
                <form onSubmit={handleCreateOrg} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Organization Name</label>
                    <Input
                      type="text"
                      placeholder="Acme Corporation"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      required
                      data-testid="input-org-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Work Email</label>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      required
                      data-testid="input-admin-email"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Password</label>
                    <Input
                      type="password"
                      placeholder="Create a strong password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      required
                      minLength={8}
                      data-testid="input-admin-password"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Data Region</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value={regionId}
                      onChange={(e) => setRegionId(e.target.value)}
                      data-testid="select-region"
                    >
                      <option value="us-east-1">🇺🇸 United States (East)</option>
                      <option value="us-west-2">🇺🇸 United States (West)</option>
                      <option value="eu-west-1">🇪🇺 Europe (Ireland)</option>
                      <option value="ap-south-1">🌏 Asia Pacific (India)</option>
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:from-purple-700 hover:via-pink-600 hover:to-purple-700 shadow-lg"
                    disabled={isLoading}
                    data-testid="button-create-org"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Free Account'}
                  </Button>
                  <p className="text-xs text-center text-gray-500 mt-4">
                    By signing up, you agree to our Terms of Service and Privacy Policy
                  </p>
                </form>
              </TabsContent>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Email Address</label>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Password</label>
                    <Input
                      type="password"
                      placeholder="Your password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      required
                      data-testid="input-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:from-purple-700 hover:via-pink-600 hover:to-purple-700 shadow-lg"
                    disabled={isLoading}
                    data-testid="button-login"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <div className="text-center">
                    <button type="button" className="text-sm text-indigo-600 hover:text-indigo-700">
                      Forgot password?
                    </button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/vivyasign_logo.png" alt="VivyaSigns" className="h-8 w-8" />
                <span className="text-xl font-bold text-white">VivyaSigns</span>
              </div>
              <p className="text-sm text-gray-400">
                The modern way to sign documents digitally. Fast, secure, and legally binding.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-indigo-400">Features</a></li>
                <li><a href="#" className="hover:text-indigo-400">Pricing</a></li>
                <li><a href="#" className="hover:text-indigo-400">Templates</a></li>
                <li><a href="#" className="hover:text-indigo-400">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-indigo-400">Documentation</a></li>
                <li><a href="#" className="hover:text-indigo-400">API Reference</a></li>
                <li><a href="#" className="hover:text-indigo-400">Help Center</a></li>
                <li><a href="#" className="hover:text-indigo-400">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-indigo-400">About Us</a></li>
                <li><a href="#" className="hover:text-indigo-400">Contact</a></li>
                <li><a href="#" className="hover:text-indigo-400">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-indigo-400">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <ComplianceBadgeStrip />
            <p className="mt-6 text-sm text-center text-gray-400">&copy; 2025 VivyaSigns. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;